import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, browserHistory } from 'react-router';
import { reduxForm, Field, change } from 'redux-form';
import { ButtonV2 } from '@zola/zola-ui';
import { toastsActions } from '@zola-helpers/client';

import { RenderCheckboxRadioField } from 'components/common/form';
import { updateSearchSettings } from 'actions/WeddingActions';
import { updateUserStatus } from 'actions/UserActions';

import 'stylesheets/pages/settings/SearchSettingsForm.less';

// Disable  camelcase rule for file. to allow for snake case elements from reducer.
/* eslint  camelcase: 0 */

class SearchSettingsForm extends Component {
  constructor(props) {
    super(props);
    this.toggleSearchEngineEnabled = this.toggleSearchEngineEnabled.bind(this);
    this.toggleZolaSearchEnabled = this.toggleZolaSearchEnabled.bind(this);
    this.handleSaveSearchSettings = this.handleSaveSearchSettings.bind(this);
  }

  toggleZolaSearchEnabled(bool) {
    this.props.onToggleZolaSearchEnabled(bool);
  }

  toggleSearchEngineEnabled(bool) {
    this.props.onToggleSearchEngineEnabled(bool);
  }

  handleSaveSearchSettings(formProps) {
    const {
      showSuccessNotification,
      showErrorNotification,
      wedding,
      onUpdateSearchSettings,
      handleUpdateUserStatus,
    } = this.props;
    const successMessage = 'Your Search Settings have been saved!';
    const errorMessage = 'There was a problem updating your settings.';
    const enable_search_engine = formProps.enableSearchEngine === 'true';
    const enable_search_zola = formProps.enableSearchZola === 'true';
    const weddingRequest = Object.assign(
      {},
      wedding,
      { enable_search_engine },
      { enable_search_zola }
    );
    handleUpdateUserStatus().then(userStatus => {
      if (userStatus.payload.status !== 'IDLE') {
        onUpdateSearchSettings(weddingRequest)
          .then(showSuccessNotification(successMessage))
          .catch(() => showErrorNotification(errorMessage));
      } else {
        browserHistory.push(`/account/login?returnTo=${window.location.pathname}`);
      }
    });
  }

  render() {
    const { handleSubmit, pristine, submitting, hasWebsite, hasRegistry } = this.props;
    const searchCopy = (
      <span>
        {hasWebsite && hasRegistry && (
          <span>
            <strong>wedding website</strong> and/or <strong>registry</strong>
          </span>
        )}
        {hasWebsite && !hasRegistry && (
          <span>
            <strong>wedding website</strong>
          </span>
        )}
        {!hasWebsite && hasRegistry && (
          <span>
            <strong>registry</strong>
          </span>
        )}
      </span>
    );

    return (
      <form
        onSubmit={handleSubmit(this.handleSaveSearchSettings)}
        className="search-settings-form row margin-bottom-2x"
      >
        <div className="col-xs-12 col-sm-8 padding-top first-q">
          <p>Do you want search engines to link to your {searchCopy}?</p>
          <div className="subtitle">
            <span>
              This may take up to 10 days.{'  '}
              <Link
                to="https://help.zola.com/hc/en-us/articles/115002148232-How-can-I-control-if-my-website-is-searchable-on-search-engines-ex-Google-or-Zola-"
                target="_blank"
                rel="noopener noreferrer"
                className="zola-ui link link-arrow"
              >
                Here&#39;s why
              </Link>
            </span>
          </div>
        </div>
        <div className="col-xs-12 col-sm-4 padding-top margin-bottom">
          <Field
            className="margin-right"
            value="true"
            component={RenderCheckboxRadioField}
            id="search-engine-yes"
            name="enableSearchEngine"
            type="radio"
            label="Yes"
            inline
            checked
            styleOverride
            onChangeAction={() => this.toggleSearchEngineEnabled('true')}
          />
          <Field
            value="false"
            component={RenderCheckboxRadioField}
            id="search-engine-no"
            name="enableSearchEngine"
            type="radio"
            label="No"
            inline
            checked
            styleOverride
            onChangeAction={() => this.toggleSearchEngineEnabled('false')}
          />
        </div>
        <br />
        <div className="col-xs-12 col-sm-8 padding-top">
          <p>Do you want your {searchCopy} to show up when guests search Zola?</p>
        </div>
        <div className="col-xs-12 col-sm-4 padding-top margin-bottom">
          <Field
            className="margin-right"
            component={RenderCheckboxRadioField}
            id="zola-search-yes"
            name="enableSearchZola"
            type="radio"
            label="Yes"
            inline
            checked
            styleOverride
            value="true"
            onChangeAction={() => this.toggleZolaSearchEnabled('true')}
          />
          <Field
            component={RenderCheckboxRadioField}
            id="zola-search-no"
            name="enableSearchZola"
            type="radio"
            label="No"
            inline
            checked
            styleOverride
            value="false"
            onChangeAction={() => this.toggleZolaSearchEnabled('false')}
          />
        </div>
        <div className="col-xs-12 padding-top">
          <ButtonV2 type="submit" className="margin-top" disabled={submitting || pristine}>
            Save Search Changes
          </ButtonV2>
        </div>
      </form>
    );
  }
}

SearchSettingsForm.propTypes = {
  handleSubmit: PropTypes.func,
  wedding: PropTypes.shape({}),
  onUpdateSearchSettings: PropTypes.func.isRequired,
  onToggleSearchEngineEnabled: PropTypes.func,
  onToggleZolaSearchEnabled: PropTypes.func,
  submitting: PropTypes.bool,
  pristine: PropTypes.bool,
  hasWebsite: PropTypes.bool,
  hasRegistry: PropTypes.bool,
  showErrorNotification: PropTypes.func,
  showSuccessNotification: PropTypes.func,
  handleUpdateUserStatus: PropTypes.func,
};

const mapStateToProps = state => {
  const { wedding } = state.wedding;
  let { enable_search_engine, enable_search_zola } = wedding;
  // Setting properties to prevent errors during initial renders (when wedding is undefined);
  if (!enable_search_engine) {
    enable_search_engine = false;
  }
  if (!enable_search_zola) {
    enable_search_zola = false;
  }
  return {
    wedding,
    initialValues: {
      enableSearchEngine: enable_search_engine.toString(),
      enableSearchZola: enable_search_zola.toString(),
    },
    hasRegistry: state.user.userContext.has_registry,
    hasWebsite: state.user.userContext.has_wedding_account,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateSearchSettings: weddingRequest => dispatch(updateSearchSettings(weddingRequest)),
  onToggleSearchEngineEnabled: isEnabled =>
    dispatch(change('searchSettingsForm', 'enableSearchEngine', isEnabled)),
  onToggleZolaSearchEnabled: isEnabled =>
    dispatch(change('searchSettingsForm', 'enableSearchZola', isEnabled)),
  showSuccessNotification: headline => dispatch(toastsActions.positive({ headline })),
  showErrorNotification: headline => dispatch(toastsActions.negative({ headline })),
  handleUpdateUserStatus: () => dispatch(updateUserStatus()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'searchSettingsForm',
    enableReinitialize: true,
  })(SearchSettingsForm)
);



// WEBPACK FOOTER //
// ./src/pages/settings/privacy/components/SearchSettingsForm.jsx